import IconStar from '@apollo/icons/default/IconStar.svg';
import IconStarSolid from '@apollo/icons/default/IconStarSolid.svg';
import {
  Admonition,
  AdmonitionBody,
  AdmonitionFooter,
  AdmonitionHeader,
  AdmonitionTitle,
  Button,
  ButtonGroup,
  IconButton,
  Textarea,
} from '@apollo/orbit';
import isChromatic from 'chromatic';
import React, { useCallback, useState } from 'react';

import { graphRefToString, useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';

/**
 * responses: https://docs.google.com/forms/d/1QzyfwlOizrcb-u3kAMWplMZ_sxUPVOlRElaUZ7gwE9U/edit#responses
 * responses sheet: https://docs.google.com/spreadsheets/d/1wU75nYuwy-9BxXPTKJZeHgKp_6vYVAnWVrfG1BpJELg/edit?resourcekey=&gid=361653391#gid=361653391
 */
function useGoogleForm() {
  const graphRef = useGraphRef();
  const [localStorageIsComplete, setLocalStorageIsComplete] = useLocalStorage(
    'hasCompletedErrorsFeedback',
  );
  const [isComplete, setIsComplete] = useState(localStorageIsComplete);
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = React.useState(false);
  const submitFeedback = useCallback(
    ({ rating, text }: { rating: number; text: string }): Promise<void> => {
      setIsFeedbackSubmitting(true);
      const formData = new FormData();
      formData.append('entry.1779000773', window.location.href);
      formData.append('entry.187527751', text);
      formData.append('entry.1787337345', rating.toString());
      if (graphRef) {
        formData.append('entry.1991037664', graphRefToString(graphRef));
      }

      if (isChromatic()) {
        setLocalStorageIsComplete(true);
        setIsFeedbackSubmitting(false);
        return Promise.resolve();
      } else {
        return fetch(
          'https://docs.google.com/forms/d/e/1FAIpQLSco6j88G968ZFpwb3Y-8MscwuqgjH5gpIk8Nw5ZDvCkUK06hQ/formResponse',
          {
            method: 'POST',
            body: formData,
            mode: 'no-cors', // Important for avoiding CORS issues
          },
        )
          .then(() => setLocalStorageIsComplete(true))
          .catch(() => {})
          .then(() => setIsFeedbackSubmitting(false));
      }
    },
    [setLocalStorageIsComplete, graphRef],
  );

  return {
    submitFeedback,
    feedbackState: isFeedbackSubmitting
      ? ('submitting' as const)
      : isComplete
        ? ('hidden' as const)
        : localStorageIsComplete
          ? ('thank-you' as const)
          : ('visible' as const),
    hideCompleteFeedbackForm: () => {
      setIsComplete(localStorageIsComplete);
    },
  };
}

function RatingButtons({
  rating,
  onRatingChange,
}: {
  rating: number;
  onRatingChange: (rating: number) => void;
}) {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  return (
    <ButtonGroup>
      {[1, 2, 3, 4, 5, 6, 7].map((id) => (
        <IconButton
          aria-label="star rating"
          variant="secondary"
          size="xs"
          key={id}
          onClick={() => onRatingChange(id)}
          onMouseEnter={() => setHoveredRating(id)}
          onMouseLeave={() => setHoveredRating(null)}
        >
          {rating >= id || (hoveredRating ?? 0) >= id ? (
            <IconStarSolid />
          ) : (
            <IconStar />
          )}
        </IconButton>
      ))}
    </ButtonGroup>
  );
}

export function ErrorsFeedbackForm() {
  const [rating, setRating] = useState<number | null>(null);
  const { submitFeedback, feedbackState, hideCompleteFeedbackForm } =
    useGoogleForm();
  const [feedbackText, setFeedbackText] = useState('');

  if (feedbackState === 'visible' || feedbackState === 'submitting') {
    return rating === null ? (
      <Admonition variant="neutral">
        <AdmonitionHeader>
          <AdmonitionTitle>
            Do you have feedback on the new errors experience?
          </AdmonitionTitle>
        </AdmonitionHeader>
        <AdmonitionBody>
          We've revamped our error insights page, and we'd love to hear your
          thoughts.
        </AdmonitionBody>
        <AdmonitionFooter>
          <RatingButtons rating={0} onRatingChange={setRating} />
        </AdmonitionFooter>
      </Admonition>
    ) : (
      <Admonition variant="neutral">
        <AdmonitionHeader>
          <AdmonitionTitle>
            What do you think about the new errors experience?
          </AdmonitionTitle>
        </AdmonitionHeader>
        <AdmonitionBody>
          <RatingButtons rating={rating} onRatingChange={setRating} />
          <Textarea
            className="mt-4"
            size="md"
            placeholder="Tell us more (optional)"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.currentTarget.value)}
          />
        </AdmonitionBody>
        <AdmonitionFooter>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => submitFeedback({ rating, text: feedbackText })}
            isLoading={feedbackState === 'submitting'}
            data-testid="submit-feedback"
          >
            Submit
          </Button>
        </AdmonitionFooter>
      </Admonition>
    );
  } else if (feedbackState === 'thank-you') {
    return (
      <Admonition variant="neutral">
        <AdmonitionHeader>
          <AdmonitionTitle>Thanks for your feedback!</AdmonitionTitle>
        </AdmonitionHeader>
        <AdmonitionBody>
          We appreciate your response and will use it to improve our product.
        </AdmonitionBody>
        <AdmonitionFooter>
          <Button
            size="sm"
            variant="secondary"
            onClick={hideCompleteFeedbackForm}
          >
            Done
          </Button>
        </AdmonitionFooter>
      </Admonition>
    );
  } else if (feedbackState === 'hidden') {
    return null;
  } else {
    return assertUnreachableOrReturnDefault(feedbackState, null);
  }
}
